<template>

    <spinner-until :condition="!showSpinner">

        <div class="survey-show mx-3 mt-page"
             v-if="!showSpinner">

            <h1>
                {{ $i18n.gt('HEADING_EDIT_SURVEY') }} #{{ survey.id }}
                ({{ survey.questionnaire_code }})
            </h1>

            <b-row :cols="$mq == 'sm' ? 1 : 2">
                <b-col>
                    <h3>{{ $i18n.gt('HEADING_SURVEY_RESPONSES') }}</h3>

                    <table class="table table-striped">
                        <tbody v-for="section in survey.questionnaire.sections" :key="section.name">
                        <tr class="section-header">
                            <th>
                                {{ $i18n.gt('LABEL_SECTION') }}: {{ section.name }}
                            </th>
                            <th class="text-center">{{ $i18n.gt('LABEL_SURVEY_TEXT') }}</th>
                            <th class="text-center">{{ $i18n.gt('LABEL_SURVEY_RESPONSE') }}</th>
                            <th class="text-center">{{ $i18n.gt('LABEL_SURVEY_POINTS') }}</th>
                        </tr>
                        <tr v-for="question in getQuestionsForSection(section)" :key="question ? question.name : ''">
                            <th>
                                {{ question.name }}
                                <div class="timestamp" v-if="question.response">
                                    #{{ question.response.id }}
                                    <br/>
                                    <timestamp :date="question.response.updated" :include-time="true"></timestamp>
                                </div>
                            </th>
                            <td class="text-center">
                                <b-badge v-if="question.response"
                                         :variant="isRecentUpdate(question.response.updated, question.name) ? 'success' : 'info'"
                                         v-b-popover.hover="$i18n.translateQuestionField(question, 'QUESTION_TEXT')">
                                    ?
                                </b-badge>
                            </td>
                            <td>
                                <div v-if="question.type == 'SELECT_ONE'">
                                    <b-form-select size="sm"
                                                   v-if="question.response"
                                                   v-model="question.response.optionValuesList[0]"
                                                   :options="getRadioOptions(question)"
                                                   @change="save()">
                                    </b-form-select>
                                </div>
                                <div v-if="question.type == 'SCALE'">
                                    <b-form-select size="sm"
                                                   v-if="question.response"
                                                   v-model="question.response.optionValuesList[0]"
                                                   :options="getScaleOptions(question)"
                                                   @change="save()">
                                    </b-form-select>
                                </div>

                                <div v-if="question.type == 'ENTER_DATA'">
                                    <div v-for="(opt, index) in getEnterDataOptions(question)"
                                         :key="index">
                                        <b-input-group class="mb-1" size="sm">
                                            <b-input-group-text slot="prepend"
                                                                v-if="opt.prefix">
                                                {{ opt.prefix }}
                                            </b-input-group-text>
                                            <!-- TODO: allow_decimal, allow_negative -->
                                            <b-form-input type="number" size="sm" min=""
                                                          :ref="index == 0 ? 'input0' : ''"
                                                          v-model="question.response.optionValuesList[index]"
                                                          @change="save()">
                                            </b-form-input>
                                            <b-input-group-text slot="append"
                                                                v-if="opt.suffix">
                                                {{ opt.suffix }}
                                            </b-input-group-text>
                                        </b-input-group>
                                    </div>
                                </div>

                                <div v-if="question.type == 'SELECT_ANY'"
                                     class="btn-group-toggle btn-group-vertical">
                                    <label v-for="(option,index) in question.options" :key="index">
                                        <b-form-checkbox size="sm"
                                                         v-model="question.response.optionValuesList[index]"
                                                         value="on"
                                                         unchecked-value=""
                                                         @input="save()">
                                            {{ translateQuestionField(question, `OPTIONS_${index}`) }}
                                        </b-form-checkbox>
                                    </label>
                                </div>

                            </td>
                            <td class="text-center">
                                <span class="response-points"
                                 v-if="showScores && question.response && question.response.points != '' && question.response.points != null">
                                  {{ round(question.response.points) }}
                                </span>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </b-col>

                <b-col>
                    <h3>{{ $i18n.gt('HEADING_SURVEY_SCORES') }}</h3>
                    <b-card no-body class="score-card"
                            v-for="scoreDef in applicableScoreDefs" :key="scoreDef.score_name">
                        <b-card-header>
                            <b-row>
                                <b-col cols="4">
                                    <strong>{{ scoreDef.score.score_name }}</strong>
                                </b-col>
                                <b-col cols="2" class="text-right">
                                    <b-badge variant="success" size="sm"
                                             v-if="scoreDef.score && isRecentUpdate(scoreDef.score.updated, scoreDef.score.score_name)">
                                        {{ $i18n.gt('LABEL_NEW') }}
                                    </b-badge>
                                </b-col>
                                <b-col cols="4" class="text-right">
                                    <div class="timestamp" v-if="scoreDef.score">
                                        #{{ scoreDef.score.id }}
                                        <timestamp class="d-inline-block"
                                                   :date="scoreDef.score.updated" :include-time="true">
                                        </timestamp>
                                    </div>
                                </b-col>
                                <b-col cols="2" class="text-right">
                                </b-col>
                            </b-row>
                        </b-card-header>
                        <b-card-body class="pt-1">

                            <report-meter :score-name="scoreDef.score.score_name">
                            </report-meter>

                            <b-row>
                                <b-col class="text-center">
                                    <label>{{ $i18n.gt('LABEL_SURVEY_THRESHOLDS') }}</label>
                                    <br/>
                                    {{ split(scoreDef.thresholds) }}
                                </b-col>
                                <b-col class="text-center">
                                    <label>{{ $i18n.gt('LABEL_SURVEY_LIMITS') }}</label>
                                    <br/>
                                    {{ split(scoreDef.limits) }}
                                </b-col>
                                <b-col class="text-center">
                                    <label>{{ $i18n.gt('LABEL_SURVEY_VALUE') }}</label>
                                    <br/>
                                    {{ round(scoreDef.score.value) }}
                                </b-col>
                                <b-col class="text-center">
                                    <label>{{ $i18n.gt('LABEL_SURVEY_POINTS') }}</label>
                                    <br/>
                                    {{ round(scoreDef.score.points) }}
                                </b-col>
                                <b-col class="text-center">
                                    <label>{{ $i18n.gt('LABEL_SURVEY_SCORE') }}</label>
                                    <br/>
                                    {{ round(scoreDef.score.score) }}
                                </b-col>
                            </b-row>
                        </b-card-body>
                    </b-card>

                </b-col>
            </b-row>

        </div>

    </spinner-until>

</template>

<!-- -------------------------------------------------------- -->

<script>
import Vue from 'vue'
import * as _ from 'lodash'
import moment from 'moment'

/**
 * Page for admin to edit responses to a Survey (using a compact form) and view and recalculate scores
 */
export default Vue.extend({
    name: 'SurveyShow',
    props: {
        surveyId: Number,
        showScores: {type: Boolean, default: true}
    },
    data() {
        return {
            showSpinner: true,
            survey: {}
        }
    },
    computed: {
        applicableScoreDefs() {
            return _.sortBy(
                _.filter(this.survey.participant.latestScoreDefs, (def) => {
                    return this.survey.questionnaire.score_names.indexOf(def.score.score_name) != -1
                }),
                (scoreDef) => {
                    return scoreDef.score_name
                })
        }
    },
    methods: {
        loadSurvey() {
            this.$store.dispatch('getSurvey', {surveyId: this.surveyId})
                .then((survey) => {
                    this.survey = this.$store.state.survey
                    this.showSpinner = false
                })
        },
        getQuestionsForSection(section) {
            const questions = _.map(section.question_names, (qname) => {
                return _.find(this.survey.questionnaire.questions, (q) => q.name === qname) ||
                    _.find(this.survey.questionnaire.scores, (q) => q.name === qname)
            })
            return _.filter(questions, (q) => {
                return q.type != 'INSTRUCTIONS'
            })
        },
        isRecentUpdate(date, name) {
            const diff = moment().diff(moment.utc(date).local(), 'minutes')
            //console.log(name, diff)
            return diff < 1
        },
        getRadioOptions(question) {
            return _.map(question.options, (opt, index) => {
                let text = this.translateQuestionField(question, `OPTIONS_${index}`)
                if (question.option_points) {
                    text += ` (${question.option_points[index]})`
                }
                return {
                    text: text,
                    value: 'r_' + index
                }
            })
        },
        getScaleOptions(question) {
            return _.map(question.options, (opt, index) => {
                return {
                    text: index + 1,
                    value: 'r_' + index
                }
            })
        },
        // generate an 'options' object for a b-form-checkbox-group
        getCheckboxOptions(question) {
            const options = question.options
            return _.map(options, (opt, index) => {
                return {
                    text: this.translateQuestionField(question, `OPTIONS_${index}`),
                    value: 'r_' + index
                }
            })
        },
        getEnterDataOptions(question) {
            const options = question.options
            return _.map(options, (opt, index) => {
                const translatedOpt = this.translateQuestionField(question, `OPTIONS_${index}`)
                if (translatedOpt) {
                    const i = translatedOpt.indexOf('__')
                    return {
                        prefix: translatedOpt.substring(0, i),
                        suffix: translatedOpt.substring(i + 2)
                    }
                } else {
                    return {prefix: '', suffix: ''}
                }
            })
        },
        clearOptionValues(question) {
            question.response.optionValuesList = _.map(question.options, (opt) => '')
        },
        round(n) {
            if (!n) {
                return ''
            }

            n = '' + n
            if (n.indexOf('.') != -1) {
                return parseFloat(n).toFixed(2)
            } else {
                return n
            }
        },
        split(vals) {
            if (vals) {
                return vals.join(' | ')
            } else {
                return null
            }
        },
        translateQuestionField(question, field) {
            return this.$i18n.gt(`QUESTION_${question.name}_${field}`)
        },
        save() {
            return this.$api.updateItem(this, 'survey', this.survey, false)
                .then(() => {
                    this.$store.dispatch('getPatientParticipant',
                        {participantId: this.survey.participant_id, force: true}
                    )
                    this.loadSurvey()
                })
                .catch((e) => {
                    this.showSpinner = false
                    this.$utils.handleError(e, 'Save error')
                })
        },
    },
    created() {
        this.loadSurvey()
    },
})

</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">

@import '../../sass/variables_mixins';

.survey-show {
    .section-header {
        th, td {
            background-color: lightblue;
        }
    }

    .score-card {
        label {
            color: #aaa;
        }
    }

    .timestamp {
        font-weight: normal;
        color: #aaa;
    }

    .meter {
        max-width: 75%;
    }
}
</style>
